// src/contexts/ImportContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Import } from "../models/Import";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { ImportService } from "../services/ImportService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { MatterService } from "../services/MatterService";

interface ImportContextProps {
  imports: Import[];
  loading: boolean;
  error: string | null;
  createFileImport: (file: globalThis.File) => Promise<void>;
  createFilingImport: (file: globalThis.File) => Promise<void>;
}

const ImportContext = createContext<ImportContextProps | undefined>(undefined);

export const useImport = (): ImportContextProps => {
  const context = useContext(ImportContext);
  if (!context) {
    throw new Error("useImport must be used within a ImportProvider");
  }
  return context;
};

export const ImportProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { currentMatterId } = useMatter();
  const [imports, setImports] = useState<Import[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    fetchImports();
  }, [user]);

  const fetchImports = async () => {
    if (!currentMatterId) return;
    try {
      const imports = await MatterService.getImports(currentMatterId);
      setImports(imports);
    } catch (err: any) {}
  };

  useSocketEventByIds("imports:changed", ["*"], (updateDocument: Import) => {
    setImports((prevImports) => {
      const existingImport = prevImports.find(
        (imp) => imp._id === updateDocument._id,
      );
      if (existingImport) {
        return prevImports.map((imp) =>
          imp._id === updateDocument._id ? updateDocument : imp,
        );
      }
      return [...prevImports, updateDocument];
    });
  });

  const createFileImport = async (file: globalThis.File): Promise<void> => {
    if (!currentMatterId) return;
    await ImportService.createFileImport(file, currentMatterId);
  };

  const createFilingImport = async (file: globalThis.File): Promise<void> => {
    if (!currentMatterId) return;
    await ImportService.createFilingImport(file, currentMatterId);
  };

  return (
    <ImportContext.Provider
      value={{
        createFileImport,
        createFilingImport,
        imports,
        loading,
        error,
      }}
    >
      {children}
    </ImportContext.Provider>
  );
};
